import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { editarJson, enviarJson, pedirJson } from "./api";
import type { EstadoDoChip } from "../componentes/ui/Chip";

/* Agenda interna de compromissos: o que a equipe vai fazer, quando, e a que chamado, ativo ou
   organização se refere. O contrato é o de `api/app/dominios/compromissos/`. Não é a Agenda de
   reuniões do cliente (`reuniao.ts`): aquela tem ata e gravação e o cliente a enxerga; esta é só
   da equipe. */

export const CHAVE_DOS_COMPROMISSOS = ["compromissos"] as const;

const uuid = z.string().uuid();

export const ESTADOS_DO_COMPROMISSO = ["agendado", "concluido", "cancelado"] as const;
export type EstadoDoCompromisso = (typeof ESTADOS_DO_COMPROMISSO)[number];

export const ROTULO_DO_ESTADO: Record<EstadoDoCompromisso, string> = {
  agendado: "Agendado",
  concluido: "Concluído",
  cancelado: "Cancelado",
};

export const CHIP_DO_ESTADO: Record<EstadoDoCompromisso, EstadoDoChip> = {
  agendado: "aguardando",
  concluido: "ok",
  cancelado: "neutro",
};

const esquemaPessoa = z.object({ id: uuid, nome: z.string() });

const esquemaCompromisso = z.object({
  id: uuid,
  titulo: z.string(),
  descricao: z.string().nullable(),
  inicio_em: z.string(),
  fim_em: z.string(),
  estado: z.enum(ESTADOS_DO_COMPROMISSO),
  responsavel: esquemaPessoa,
  organizacao: z.object({ id: uuid, nome: z.string() }).nullable(),
  ticket: z.object({ id: uuid, numero: z.number().int(), assunto: z.string() }).nullable(),
  ativo: z.object({ id: uuid, nome: z.string() }).nullable(),
  concluido_em: z.string().nullable(),
  resultado: z.string().nullable(),
  cancelado_em: z.string().nullable(),
  remarcacoes: z.number().int(),
  lembrete_minutos: z.number().int().nullable(),
  lembrete_enviado_em: z.string().nullable(),
});

const esquemaApoio = z.object({
  responsaveis: z.array(esquemaPessoa),
  organizacoes: z.array(z.object({ id: uuid, nome: z.string() })),
  ativos: z.array(z.object({ id: uuid, nome: z.string() })),
});

export type Compromisso = z.infer<typeof esquemaCompromisso>;
export type ApoioDaAgenda = z.infer<typeof esquemaApoio>;

/** Quanto antes o lembrete por e-mail sai. Vazio = sem lembrete. */
export const OPCOES_DE_LEMBRETE: ReadonlyArray<{ minutos: number | null; rotulo: string }> = [
  { minutos: null, rotulo: "Sem lembrete" },
  { minutos: 15, rotulo: "15 minutos antes" },
  { minutos: 30, rotulo: "30 minutos antes" },
  { minutos: 60, rotulo: "1 hora antes" },
  { minutos: 1440, rotulo: "1 dia antes" },
];

export function lembreteLegivel(minutos: number | null): string {
  return OPCOES_DE_LEMBRETE.find((o) => o.minutos === minutos)?.rotulo ?? `${minutos} minutos antes`;
}

/* ------------------------------------------------------------------------------ calendário */



/** Segunda-feira, 00:00 (hora local), da semana que contém a data. */
export function inicioDaSemana(data: Date): Date {
  const dia = new Date(data.getFullYear(), data.getMonth(), data.getDate());
  const deslocamento = (dia.getDay() + 6) % 7; // segunda = 0 ... domingo = 6
  return somarDias(dia, -deslocamento);
}

/** Soma dias no calendário local (um dia de horário de verão não vira 23 ou 25 horas). */
export function somarDias(data: Date, dias: number): Date {
  return new Date(data.getFullYear(), data.getMonth(), data.getDate() + dias, 0, 0, 0, 0);
}

export function diasDaSemana(inicio: Date): Date[] {
  return Array.from({ length: 7 }, (_, indice) => somarDias(inicio, indice));
}

/** `2026-10-05`, na hora local. */
export function chaveDoDia(data: Date): string {
  const mes = String(data.getMonth() + 1).padStart(2, "0");
  const dia = String(data.getDate()).padStart(2, "0");
  return `${data.getFullYear()}-${mes}-${dia}`;
}

const NOMES_DOS_DIAS = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];

export function rotuloDoDia(data: Date): string {
  const dia = String(data.getDate()).padStart(2, "0");
  const mes = String(data.getMonth() + 1).padStart(2, "0");
  return `${NOMES_DOS_DIAS[data.getDay()]} ${dia}/${mes}`;
}

export function rotuloDaSemana(inicio: Date): string {
  const fim = somarDias(inicio, 6);
  const curto = (d: Date) =>
    `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}`;
  return `${curto(inicio)} a ${curto(fim)}/${fim.getFullYear()}`;
}

export function horaLegivel(iso: string): string {
  const data = new Date(iso);
  if (Number.isNaN(data.getTime())) return "—";
  return `${String(data.getHours()).padStart(2, "0")}:${String(data.getMinutes()).padStart(2, "0")}`;
}

export function dataHoraLegivel(iso: string | null): string {
  if (!iso) return "—";
  const data = new Date(iso);
  if (Number.isNaN(data.getTime())) return "—";
  return `${rotuloDoDia(data)} às ${horaLegivel(iso)}`;
}

/**
 * Agrupa por dia da semana mostrada. O compromisso que começou antes da semana (e a atravessa)
 * cai no primeiro dia; o de fora da semana não entra. Dentro do dia, por horário.
 */
export function agruparPorDia(
  compromissos: readonly Compromisso[],
  inicio: Date,
): Map<string, Compromisso[]> {
  const grupos = new Map<string, Compromisso[]>(
    diasDaSemana(inicio).map((dia) => [chaveDoDia(dia), []]),
  );
  const primeiro = chaveDoDia(inicio);
  for (const item of compromissos) {
    const quando = new Date(item.inicio_em);
    const chave = quando < inicio ? primeiro : chaveDoDia(quando);
    grupos.get(chave)?.push(item);
  }
  for (const lista of grupos.values()) {
    lista.sort((a, b) => a.inicio_em.localeCompare(b.inicio_em) || a.id.localeCompare(b.id));
  }
  return grupos;
}

/** Os limites da consulta de uma semana, em ISO (a API compara instantes). */
export function intervaloDaSemana(inicio: Date): { de: string; ate: string } {
  return { de: inicio.toISOString(), ate: somarDias(inicio, 7).toISOString() };
}

/* --------------------------------------------------------------------------- formulário */

export interface FormularioDeCompromisso {
  titulo: string;
  descricao: string;
  /** `datetime-local`, na hora local. */
  inicio: string;
  fim: string;
  responsavelId: string;
  organizacaoId: string;
  /** O número que a equipe vê (123 ou #123). */
  ticketNumero: string;
  ativoId: string;
  lembreteMinutos: string;
}

function paraDatetimeLocal(data: Date): string {
  const dia = chaveDoDia(data);
  return `${dia}T${String(data.getHours()).padStart(2, "0")}:${String(data.getMinutes()).padStart(2, "0")}`;
}

/** Em branco, na próxima hora cheia depois de `agora`, com uma hora de duração. */
export function formularioEmBranco(agora: Date = new Date()): FormularioDeCompromisso {
  const inicio = new Date(agora);
  inicio.setMinutes(0, 0, 0);
  inicio.setHours(inicio.getHours() + 1);
  const fim = new Date(inicio.getTime() + 60 * 60 * 1000);
  return {
    titulo: "",
    descricao: "",
    inicio: paraDatetimeLocal(inicio),
    fim: paraDatetimeLocal(fim),
    responsavelId: "",
    organizacaoId: "",
    ticketNumero: "",
    ativoId: "",
    lembreteMinutos: "",
  };
}

export function formularioDoCompromisso(c: Compromisso): FormularioDeCompromisso {
  return {
    titulo: c.titulo,
    descricao: c.descricao ?? "",
    inicio: paraDatetimeLocal(new Date(c.inicio_em)),
    fim: paraDatetimeLocal(new Date(c.fim_em)),
    responsavelId: c.responsavel.id,
    organizacaoId: c.organizacao?.id ?? "",
    ticketNumero: c.ticket ? String(c.ticket.numero) : "",
    ativoId: c.ativo?.id ?? "",
    lembreteMinutos: c.lembrete_minutos === null ? "" : String(c.lembrete_minutos),
  };
}

function numeroDoChamado(texto: string): number | null {
  const limpo = texto.trim().replace(/^#/, "");
  return /^\d+$/.test(limpo) && Number(limpo) > 0 ? Number(limpo) : null;
}

export function validarHorario(inicio: string, fim: string): string | null {
  const de = new Date(inicio);
  const ate = new Date(fim);
  if (!inicio || !fim || Number.isNaN(de.getTime()) || Number.isNaN(ate.getTime())) {
    return "Informe quando começa e quando termina.";
  }
  if (ate <= de) return "O compromisso precisa terminar depois de começar.";
  return null;
}

export function validarCompromisso(form: FormularioDeCompromisso, comHorario = true): string | null {
  if (!form.titulo.trim()) return "Dê um título ao compromisso.";
  if (comHorario) {
    const horario = validarHorario(form.inicio, form.fim);
    if (horario) return horario;
  }
  if (form.ticketNumero.trim() && numeroDoChamado(form.ticketNumero) === null) {
    return "O número do chamado é só dígitos, como em #123.";
  }
  return null;
}

function vinculos(form: FormularioDeCompromisso) {
  return {
    organizacao_id: form.organizacaoId || null,
    ticket_numero: numeroDoChamado(form.ticketNumero),
    ativo_id: form.ativoId || null,
    lembrete_minutos: form.lembreteMinutos ? Number(form.lembreteMinutos) : null,
  };
}

export function corpoDaCriacao(form: FormularioDeCompromisso): Record<string, unknown> {
  return {
    titulo: form.titulo.trim(),
    descricao: form.descricao.trim() || null,
    inicio_em: new Date(form.inicio).toISOString(),
    fim_em: new Date(form.fim).toISOString(),
    responsavel_id: form.responsavelId || null,
    ...vinculos(form),
  };
}

/** A edição não mexe no horário (para isso existe "Remarcar"). */
export function corpoDaEdicao(form: FormularioDeCompromisso): Record<string, unknown> {
  return {
    titulo: form.titulo.trim(),
    descricao: form.descricao.trim() || null,
    ...(form.responsavelId ? { responsavel_id: form.responsavelId } : {}),
    ...vinculos(form),
  };
}

/* ---------------------------------------------------------------------------- consultas */

export interface FiltrosDosCompromissos {
  de?: string;
  ate?: string;
  responsavelId?: string;
  organizacaoId?: string;
  ticketId?: string;
  ativoId?: string;
  estado?: EstadoDoCompromisso;
}

export function useCompromissos(filtros: FiltrosDosCompromissos, habilitado = true) {
  const parametros = new URLSearchParams();
  if (filtros.de) parametros.set("de", filtros.de);
  if (filtros.ate) parametros.set("ate", filtros.ate);
  if (filtros.responsavelId) parametros.set("responsavel_id", filtros.responsavelId);
  if (filtros.organizacaoId) parametros.set("organizacao_id", filtros.organizacaoId);
  if (filtros.ticketId) parametros.set("ticket_id", filtros.ticketId);
  if (filtros.ativoId) parametros.set("ativo_id", filtros.ativoId);
  if (filtros.estado) parametros.set("estado", filtros.estado);
  const consulta = parametros.toString();
  return useQuery({
    queryKey: [...CHAVE_DOS_COMPROMISSOS, "lista", consulta],
    enabled: habilitado,
    queryFn: ({ signal }) =>
      pedirJson(`/compromissos?${consulta}`, z.array(esquemaCompromisso), { signal }),
  });
}

export function useApoioDaAgenda(organizacaoId: string, habilitado = true) {
  return useQuery({
    queryKey: [...CHAVE_DOS_COMPROMISSOS, "apoio", organizacaoId],
    enabled: habilitado,
    queryFn: ({ signal }) =>
      pedirJson(
        organizacaoId
          ? `/compromissos/apoio?organizacao_id=${encodeURIComponent(organizacaoId)}`
          : "/compromissos/apoio",
        esquemaApoio,
        { signal },
      ),
  });
}

function useMutacaoDeCompromisso<T>(funcao: (entrada: T) => Promise<Compromisso>) {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: funcao,
    // Compromisso concluído vira contato na saúde do cliente: o cálculo vê no próximo recálculo.
    onSuccess: () => void cliente.invalidateQueries({ queryKey: CHAVE_DOS_COMPROMISSOS }),
  });
}

export function useCriarCompromisso() {
  return useMutacaoDeCompromisso((corpo: Record<string, unknown>) =>
    enviarJson("/compromissos", esquemaCompromisso, corpo),
  );
}

export function useAlterarCompromisso() {
  return useMutacaoDeCompromisso(({ id, corpo }: { id: string; corpo: Record<string, unknown> }) =>
    editarJson(`/compromissos/${encodeURIComponent(id)}`, esquemaCompromisso, corpo),
  );
}

export function useRemarcarCompromisso() {
  return useMutacaoDeCompromisso(({ id, inicio, fim }: { id: string; inicio: string; fim: string }) =>
    enviarJson(`/compromissos/${encodeURIComponent(id)}/remarcacao`, esquemaCompromisso, {
      inicio_em: new Date(inicio).toISOString(),
      fim_em: new Date(fim).toISOString(),
    }),
  );
}

export function useConcluirCompromisso() {
  return useMutacaoDeCompromisso(({ id, resultado }: { id: string; resultado: string }) =>
    enviarJson(`/compromissos/${encodeURIComponent(id)}/conclusao`, esquemaCompromisso, {
      resultado: resultado.trim() || null,
    }),
  );
}

export function useCancelarCompromisso() {
  return useMutacaoDeCompromisso((id: string) =>
    enviarJson(`/compromissos/${encodeURIComponent(id)}/cancelamento`, esquemaCompromisso, {}),
  );
}


